import { App } from '../../App';
import { DEFAULT_TERM, IS_VIVUS } from 'utils/constants';
import {
  FrameCalculatorDesktop,
  FrameCalculatorMobile,
} from 'components/organisms/Frame/FrameCalculator';
import { getAmountAndTermCalculator } from '../../hooks/useApplicationSummary';
import {
  RegistrationCalculatorComponentProps,
  RegistrationGlobalComponentProps,
} from 'types/components';
import { setDefaultCalculatorFromSession } from 'utils/constantsSession';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useErrorManager } from '../../InternalLibrary';
import { useGetClientApplication } from '../../api/atoms';
import { useTranslation } from 'react-i18next';
import useClient from '../../hooks/useClient';
import useClientConstraints from '../../hooks/useClientConstraints';
import useClientOffer from '../../hooks/useClientOffer';

export type OfferQueryParametersRaw = {
  amount?: string | null;
  term?: string | null;
};

export type OfferQueryParameters = {
  amount: number;
  term: number;
};

export const sanitizeOfferQueryParameters = (
  { amount, term }: OfferQueryParametersRaw,
  defaultParameters: OfferQueryParameters,
): OfferQueryParameters => ({
  amount: amount != null ? Number(amount) : defaultParameters.amount,
  term: App.config.term || term != null ? Number(term) : defaultParameters.term,
});

export const useCalculatorSharedLogic = ({
  onCalculatorClose,
}: {
  onCalculatorClose: RegistrationGlobalComponentProps['onCalculatorClose'];
}) => {
  const { t } = useTranslation();
  const { data: dataOffer, fetch: fetchClientOffer } = useClientOffer();
  const { data: constraintsData } = useClientConstraints();
  const { client } = useClient();
  const apiApplication = useGetClientApplication();
  const { setError } = useErrorManager();

  const calculatorDefaultValues = getAmountAndTermCalculator({
    constraints: constraintsData,
    application: apiApplication.data,
  });

  // order -> offer response, application data, session, default value from constraints
  const amount = calculatorDefaultValues?.amount;
  const days = calculatorDefaultValues?.term;

  const [dataCalculator, setDataCalculator] = useState({ amount, days });

  useEffect(() => {
    if (dataCalculator.amount !== amount || dataCalculator.days !== days) {
      setDataCalculator({ amount, days });
    }
  }, [amount, days]);

  const constraints = useMemo(() => {
    if (
      !constraintsData?.amountInterval ||
      !constraintsData.termInterval ||
      !apiApplication.data
    )
      return undefined;

    const term =
      App.config.term !== undefined
        ? {
            min: App.config.term,
            max: App.config.term,
            step: 0,
            defaultValue: calculatorDefaultValues?.term,
          }
        : {
            min: constraintsData.termInterval.min,
            max: constraintsData.termInterval.max,
            step: constraintsData.termInterval.step,
            defaultValue: calculatorDefaultValues?.term,
          };

    const firstLoanAmountLimit =
      constraintsData?.loanLimits?.[
        1 as keyof typeof constraintsData.loanLimits
      ];

    const amount = {
      ...constraintsData.amountInterval,
      max: firstLoanAmountLimit
        ? firstLoanAmountLimit
        : constraintsData.termInterval.max,
      defaultValue: firstLoanAmountLimit
        ? firstLoanAmountLimit
        : calculatorDefaultValues?.term,
    };

    if (client?.dateOfBirth && client?.creditLimit)
      return {
        term,
        amount: {
          ...amount,
          max: client?.creditLimit,
          defaultValue: client?.creditLimit,
        },
      };

    return { term, amount };
  }, [client?.creditLimit, client?.dateOfBirth, constraintsData]);

  const handleSubmit = useCallback(
    async (data: { amount: number; days: number }) => {
      try {
        await fetchClientOffer({
          queryParameters: {
            amount: data.amount,
            term: data?.days || DEFAULT_TERM,
          },
        });
        setDefaultCalculatorFromSession(data.amount, data?.days);
        onCalculatorClose();
      } catch (e) {
        setError(e);
      }
    },
    [onCalculatorClose, setError],
  );

  return {
    hideDays: !IS_VIVUS,
    content: {
      title: t('common:calculator.title'),
      amountTitle: t('registration_header:title_amount'),
      daysTitle: t('registration_header:title_days'),
      amountUnit: t('common:currencySymbol'),
      daysUnit: t('common:days'),
      confirmChanges: t('common:calculator.confirmChanges'),
      openLoanInformationModal: t('common:calculator.openLoanInformationModal'),
      swiperQuantity: t('common:calculator.swiperQuantity'),
      swiperTerm: t('common:calculator.swiperTerm'),
    },
    constraints,
    data: Boolean(dataOffer)
      ? {
          amount: dataCalculator.amount,
          days: dataCalculator.days,
        }
      : undefined,
    onSubmit: handleSubmit,
  };
};

export const CalculatorDesktop = ({
  onCalculatorClose,
}: RegistrationCalculatorComponentProps) => {
  const calculatorProps = useCalculatorSharedLogic({
    onCalculatorClose,
  });

  return <FrameCalculatorDesktop {...calculatorProps} />;
};

export const CalculatorMobile = ({
  onCalculatorClose,
}: RegistrationCalculatorComponentProps) => {
  const calculatorProps = useCalculatorSharedLogic({
    onCalculatorClose,
  });

  return <FrameCalculatorMobile {...calculatorProps} />;
};
